
/**
 * A utility class for creating a responsive masonry grid layout.
 * It dynamically arranges grid items into columns based on the container's width
 * and supports lazy-loading more items with a "Load More" button.
 *
 * This class can handle two types of content:
 * 1. Static HTML already present in the DOM when the page loads.
 * 2. Dynamic content fetched asynchronously from a server via a provided function.
 */
export default class MasonryGrid {
    /**
     * @private
     * The constructor should be considered private. Use the static `create` method for instantiation.
     * This constructor handles only synchronous setup and initial state, deferring all
     * asynchronous operations to the `init` method.
     */
    constructor(fetchFn, containerId, buttonId, loadItemsCount, itemClassSelector, placeholderHtml) {
        this.$originalContainerEl = $(`#${containerId}`);
        this.$loadMoreButtonEl = buttonId ? $(`#${buttonId}`) : null;
        this.$loadMoreBtnText = this.$loadMoreButtonEl.length ? this.$loadMoreButtonEl.text() : 'Load more';

        this.loadItemsCount = loadItemsCount;
        this.maxColumns = 4;
        this.minColumnWidth = 250;
        this.currentPage = 0;
        this.totalCount = 0;

        this.itemClassSelector = itemClassSelector;
        this.fetchItemsFn = fetchFn;
        this.useDynamicContent = typeof this.fetchItemsFn === 'function';
        
        this.columnEls = [];
        this.allGridItems = [];
        this.otherElements = [];
        this.staticGridItems = [];

        this.placeholderHtml = placeholderHtml ?? `<div class="${this.itemClassSelector.replace(/^\.+/, '') } placeholder-glow">
            <div class="placeholder-box placeholder-image"></div>
            <div class="placeholder-box" style="width: 80%;"></div>
            <div class="placeholder-box placeholder-sm" style="width: 50%;"></div>
            <div class="placeholder-box placeholder-sm" style="width: 50%;"></div>
            <div class="placeholder-box placeholder-sm" style="width: 50%;"></div>
        </div>`;
    }

    /**
     * The recommended and asynchronous way to create a new MasonryGrid instance.
     * This static factory method ensures that the instance is fully initialized with
     * all data (both static and dynamic) before it is returned.
     * @param {function(number, number, number): Promise<Object> | null} [fetchFn=null] An async function that fetches dynamic content. It should accept page, initial count, and reload count, and return a Promise resolving to an object with newTiles, otherChildren, and totalCount.
     * @param {string} [containerId='masonry-grid'] The ID of the HTML element that acts as the grid container.
     * @param {string} [buttonId='masonry-grid-more-btn'] The ID of the "Load More" button.
     * @param {number} [loadItemsCount=1] The number of new items to load initially and when the "Load More" button is clicked.
     * @param {string} [itemClassSelector='.grid-item'] The CSS selector for the individual grid items.
     * @returns {Promise<MasonryGrid>} A Promise that resolves with the fully initialized MasonryGrid instance.
     */
    static async create(fetchFn = null, containerId = 'masonry-grid', buttonId = 'masonry-grid-more-btn', loadItemsCount = 30, itemClassSelector = '.masonry-grid-item', placeholderHtml) {
        // here masonry grid has children
        const instance = new MasonryGrid(fetchFn, containerId, buttonId, loadItemsCount, itemClassSelector, placeholderHtml);
        // here masonry grid does not
        await instance.init();
        return instance;
    }

    /**
     * Initializes the grid. This method handles asynchronous data fetching,
     * populating the grid with initial content, and setting up event listeners.
     * It correctly awaits the provided `fetchFn` if dynamic content is used,
     * ensuring the grid is not rendered until the data is available.
     * It also manages static grid items, preserving them across dynamic reloads.
     * @param {function(number, number, ...*): Promise<Object>} [fetchFn=this.fetchItemsFn] An async function to fetch grid items.
     * @param {boolean} [isReload=false] A flag to determine if the call is a reload, which affects how existing items are handled.
     * @returns {Promise<void>}
     */
    async init(fetchFn = this.fetchItemsFn, isReload = false) {
        this.allGridItems = this.$originalContainerEl.find(this.itemClassSelector).toArray().map(item => $(item).clone(true, true)[0]);
        this.otherElements = this.$originalContainerEl.children().not(this.itemClassSelector).toArray().map(el => $(el).clone(true, true)[0]);
        this.totalCount = this.allGridItems.length;
        
        if (this.useDynamicContent) {
            this.currentPage = 1;

            // add skeleton children as placeholders to make a grid of 6 items
            const originalChildrenCount = this.$originalContainerEl.find(this.itemClassSelector).length;

            for(let i = 0; i < (6 - originalChildrenCount); i++) {
                this.$originalContainerEl.addClass('has-placeholder').append(this.placeholderHtml);
            }       
            
            const fnReturn = await fetchFn(this.currentPage, this.loadItemsCount);

            if(isReload) {
                this.allGridItems.push(...this.staticGridItems);
            } else {
                // first time through store the original grid items so 
                // they don't get lost when relaoding dynamic content 
                this.staticGridItems = [...this.allGridItems];

            }

            this.allGridItems.push(...fnReturn.newTiles);
            this.otherElements.push(...fnReturn.otherChildren);
            this.totalCount = fnReturn.totalCount + this.staticGridItems.length;
        }

        this.$originalContainerEl.empty().removeClass('has-placeholder');
        this.buildLayoutStructure();
        this.render(this.loadItemsCount);
        this.setupEventListeners();
    }

    /**
     * Sets up event listeners for window resize and the "Load More" button.
     * @returns {void}
     */
    setupEventListeners() {
        let lastNumColumns = this.columnEls.length;

        $(window).on('resize', () => {
            const newNumColumns = this.calcColumnCount();
            if (newNumColumns !== lastNumColumns) {
                const allVisibleItemsCount = this.$wrapper.find(this.itemClassSelector).length;
                this.render(allVisibleItemsCount);
                lastNumColumns = newNumColumns;
            }
        });

        if (this.$loadMoreButtonEl.length) {
            this.$loadMoreButtonEl.on('click', () => {
                this.loadMoreItems();
            });
        }
    }

    /**
     * Dynamically creates the necessary wrapper, masonry container, and columns.
     * It also appends a "Load More" button if one is not already present.
     * @returns {void}
     */
    buildLayoutStructure() {
        this.$wrapper = $('<div>').addClass('masonry-grid-wrapper');
        this.$masonryContainer = $('<div>').addClass('masonry-grid-container');

        this.$wrapper.append(this.$masonryContainer);
        this.otherElements.forEach(el => this.$wrapper.append($(el).clone(true, true)[0]));

        if (!this.$loadMoreButtonEl.length) {
            this.$loadMoreButtonEl = $('<button>').attr({
                'id':'masonry-grid-more-btn',
                'class': 'btn-primary'
            }).text(this.$loadMoreBtnText);
            this.$wrapper.append($('<div>').addClass('masonry-grid-more-btn-container').append(this.$loadMoreButtonEl));
        }

        this.$originalContainerEl.append(this.$wrapper);
    }

    /**
     * Finds the column with the least height in the grid.
     * This is used to determine where to place the next grid item.
     * @returns {HTMLElement|null} The shortest column element, or null if no columns exist.
     */
    getShortestColumn() {
        if (!this.columnEls.length) return null;

        return this.columnEls.reduce((shortest, current) => {
            return current.offsetHeight < shortest.offsetHeight ? current : shortest;
        }, this.columnEls[0]);
    }

    /**
     * Renders the grid with a specified number of items and columns, and manages the visibility of the "Load More" button.
     * @param {number} [itemsCount=this.loadItemsCount] The number of items to render on the page.
     * @returns {void}
     */
    render(itemsCount = this.loadItemsCount) {
        if (!this.$loadMoreButtonEl.length) {
            itemsCount = this.totalCount;
        }

        const numColumns = this.calcColumnCount();
        let itemsToRender;

        this.$masonryContainer.empty();
        this.columnEls = Array.from({ length: numColumns }, () => $('<div>').addClass('masonry-grid-column').appendTo(this.$masonryContainer)[0]);

        if(this.useDynamicContent) {
            itemsToRender = this.allGridItems;
            
        } else {
            itemsToRender = this.allGridItems.slice(0, itemsCount);
        }

        itemsToRender?.forEach(item => {
            window.requestAnimationFrame(() => {
                const shortestColumn = this.getShortestColumn();
                shortestColumn.appendChild($(item).clone(true, true)[0]);
            });
        });

        if (itemsToRender.length >= this.totalCount) {
            this.$loadMoreButtonEl.hide();
        } else {
            this.$loadMoreButtonEl.show();
        }
    }

    /**
     * Loads and renders a new batch of items when the "Load More" button is clicked.
     * This function handles the loading state, fetches new data asynchronously,
     * appends the new items to the grid, and updates the "Load More" button's visibility.
     * @returns {Promise<void>}
     */
    async loadMoreItems() {
        let itemsToLoad;
        const currentItemCount = this.$masonryContainer.find(this.itemClassSelector).length;

        if (this.useDynamicContent) {
            this.$loadMoreButtonEl.text('Loading...');
            this.$loadMoreButtonEl.prop('disabled', true);

            try {
                this.currentPage++;
                const fnReturn = await this.fetchItemsFn(this.currentPage, this.loadItemsCount);
                const newOtherEls = fnReturn.otherChildren;
                itemsToLoad = fnReturn.newTiles;
                this.allGridItems.push(...itemsToLoad);
                
                if (newOtherEls.length){
                    const uniqueNewEls = newOtherEls.filter(newEl => {
                        return !this.otherElements.some(existingEl => existingEl.isEqualNode(newEl));
                    });

                    this.otherElements.push(...uniqueNewEls);
                    uniqueNewEls.forEach(el => this.$wrapper.append($(el).clone(true, true)[0]));
                }

            } catch (error) {
                console.error('Failed to load more items:', error);

                this.$loadMoreButtonEl.text(this.$loadMoreBtnText);
                this.$loadMoreButtonEl.prop('disabled', false);
                return;
            } finally {
                this.$loadMoreButtonEl.text(this.$loadMoreBtnText);
                this.$loadMoreButtonEl.prop('disabled', false);
            }
        } else {
            itemsToLoad = this.allGridItems.slice(currentItemCount, currentItemCount + this.loadItemsCount);
        }

        itemsToLoad.forEach(item => {
            window.requestAnimationFrame(() => {
                const shortestColumn = this.getShortestColumn();
                if (shortestColumn) {
                    shortestColumn.appendChild($(item).clone(true, true)[0]);
                }
            });
        });

        if (currentItemCount + itemsToLoad.length >= this.totalCount) {
            this.$loadMoreButtonEl.hide();
        }
    }

    getResponsiveColumnCount() {
        const windowWidth = $(window).width();

        // Define the column count based on standard bs5 esponsive breakpoints:
        if (windowWidth >= 992) {
            return 4;
        } else if (windowWidth >= 768) {
            return 3; 
        } else if (windowWidth >= 576) {
            return 2;
        } else {
            return 1;
        }
    }

    /**
     * Calculates the ideal number of columns based on the container's width.
     * The number of columns will be at least 1.
     * @returns {number} The calculated number of columns.
     */
    calcColumnCount() {

        const currentContainerWidth = this.$masonryContainer.width();
        const targetColumns = this.getResponsiveColumnCount();

        const calculatedColumns = Math.floor(currentContainerWidth / this.minColumnWidth);

        // Enforce the constraints set by the breakpoints logic.
        // This ensures that even if the calculation allows 5 columns (1400/280), 
        // it gets clamped to the max of 4 defined in the breakpoint logic.
        return Math.max(1, Math.min(targetColumns, calculatedColumns));
    }

    /**
     * Asynchronously reloads the grid with a fresh set of data.
     * This is useful for when filters or other parameters change.
     * @param {Object} [queryParams={}] An object of key-value pairs to pass to the fetch function.
     * @returns {Promise<void>}
     */
    async reload(queryParams = {}) {

        if (!this.useDynamicContent) {
            console.warn("Reload is only supported for dynamic content grids.");
            return;
        }

        // Pass the queryParams as a closure to the fetch function
        const fetchWithParams = (start, limit) => this.fetchItemsFn(start, limit, ...Object.values(queryParams));

        // Reset the internal state
        this.currentPage = 0;
        this.allGridItems = [];
        this.otherElements = [];
        this.totalCount = 0;

        this.$originalContainerEl.empty().addClass('has-placeholder');

        // Re-initialize the grid with the new data
        await this.init(fetchWithParams, true);
    }
}